import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import remindersService, {
  type CreateReminderData,
  type UpdateReminderData,
  type ReminderDateRangeParams,
} from '@/services/reminders.service';

export const REMINDERS_QUERY_KEYS = {
  all: ['reminders'] as const,
  byTicket: (ticketId: string) => ['reminders', 'ticket', ticketId] as const,
  byDateRange: (params: ReminderDateRangeParams) => ['reminders', 'dateRange', params] as const,
  byId: (id: string) => ['reminders', id] as const,
};

/**
 * Get reminders for a specific ticket
 */
export function useRemindersByTicket(ticketId: string) {
  return useQuery({
    queryKey: REMINDERS_QUERY_KEYS.byTicket(ticketId),
    queryFn: () => remindersService.getRemindersByTicket(ticketId),
    enabled: !!ticketId,
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchOnWindowFocus: false,
  });
}

/**
 * Get reminders by date range
 */
export function useRemindersByDateRange(params: ReminderDateRangeParams) {
  return useQuery({
    queryKey: REMINDERS_QUERY_KEYS.byDateRange(params),
    queryFn: () => remindersService.getRemindersByDateRange(params),
    enabled: !!params.startDate && !!params.endDate,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Get a specific reminder by ID
 */
export function useReminderById(id: string) {
  return useQuery({
    queryKey: REMINDERS_QUERY_KEYS.byId(id),
    queryFn: () => remindersService.getReminderById(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Create a new reminder
 */
export function useCreateReminder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateReminderData) => remindersService.createReminder(data),
    onSuccess: (newReminder) => {
      // Invalidate ticket reminders
      queryClient.invalidateQueries({
        queryKey: REMINDERS_QUERY_KEYS.byTicket(newReminder.ticket_id),
      });

      // Invalidate date range queries that might include this reminder
      queryClient.invalidateQueries({
        queryKey: ['reminders', 'dateRange'],
      });
    },
    onError: (error) => {
      console.error('Failed to create reminder:', error);
    },
  });
}

/**
 * Update an existing reminder
 */
export function useUpdateReminder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateReminderData }) =>
      remindersService.updateReminder(id, data),
    onSuccess: (updatedReminder) => {
      // Invalidate specific reminder query
      queryClient.invalidateQueries({
        queryKey: REMINDERS_QUERY_KEYS.byId(updatedReminder.id),
      });

      // Invalidate ticket reminders
      queryClient.invalidateQueries({
        queryKey: REMINDERS_QUERY_KEYS.byTicket(updatedReminder.ticket_id),
      });

      // Invalidate date range queries
      queryClient.invalidateQueries({
        queryKey: ['reminders', 'dateRange'],
      });
    },
    onError: (error) => {
      console.error('Failed to update reminder:', error);
    },
  });
}

/**
 * Delete a reminder
 */
export function useDeleteReminder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => remindersService.deleteReminder(id),
    onSuccess: (_, reminderId) => {
      // Invalidate all reminder queries
      queryClient.invalidateQueries({
        queryKey: REMINDERS_QUERY_KEYS.all,
      });
    },
    onError: (error) => {
      console.error('Failed to delete reminder:', error);
    },
  });
}

/**
 * Mark reminder as completed
 */
export function useCompleteReminder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => remindersService.completeReminder(id),
    onSuccess: (updatedReminder) => {
      // Update cache optimistically
      queryClient.setQueryData(
        REMINDERS_QUERY_KEYS.byId(updatedReminder.id),
        updatedReminder
      );

      // Invalidate ticket reminders
      queryClient.invalidateQueries({
        queryKey: REMINDERS_QUERY_KEYS.byTicket(updatedReminder.ticket_id),
      });

      // Invalidate date range queries
      queryClient.invalidateQueries({
        queryKey: ['reminders', 'dateRange'],
      });
    },
    onError: (error) => {
      console.error('Failed to complete reminder:', error);
    },
  });
}

/**
 * Mark reminder as not completed
 */
export function useUncompleteReminder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => remindersService.uncompleteReminder(id),
    onSuccess: (updatedReminder) => {
      // Update cache optimistically
      queryClient.setQueryData(
        REMINDERS_QUERY_KEYS.byId(updatedReminder.id),
        updatedReminder
      );

      // Invalidate ticket reminders
      queryClient.invalidateQueries({
        queryKey: REMINDERS_QUERY_KEYS.byTicket(updatedReminder.ticket_id),
      });

      // Invalidate date range queries
      queryClient.invalidateQueries({
        queryKey: ['reminders', 'dateRange'],
      });
    },
    onError: (error) => {
      console.error('Failed to uncomplete reminder:', error);
    },
  });
}
